import { Fragment, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, Crosshair } from "lucide-react";
import { useTourManager } from "../hooks/useTourManager";
import { TourEngine } from "./onboarding/TourEngine";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { formatCurrency, MONTH_NAMES } from "../lib/calculations";
import { useToast } from "./ui/ToastModal";
import {
  TargetNotificationPopup,
  type TargetNotification,
} from "./ui/TargetNotificationPopup";
import type {
  ClaimsProgress,
  ClaimsCategory,
  ClaimsSubcategory,
} from "../types/database.types";
import {
  fetchPaymentLineItemsByProject,
  buildPaymentCellMap,
  type PaymentCellInfo,
  getActionColor,
  getActionLabel,
} from "../lib/paymentLineItems";
import { logError, logWarn } from "../lib/logError";

interface CategoryWithSubs extends ClaimsCategory {
  subcategories: ClaimsSubcategory[];
}

interface ProjectWithDetails {
  id: string;
  categories: CategoryWithSubs[];
}

interface ProgressRangeViewProps {
  project: ProjectWithDetails;
  progress: ClaimsProgress[];
  onProgressUpdate: (progress: ClaimsProgress[]) => void;
  highlightSubcategoryId?: string;
  highlightCategoryId?: string;
}

// Category color palette - VIBRANT
const CATEGORY_COLORS = [
  { bg: "#dbeafe", border: "#3b82f6", text: "#1e40af", headerBg: "#93c5fd" },
  { bg: "#d1fae5", border: "#10b981", text: "#065f46", headerBg: "#6ee7b7" },
  { bg: "#fef3c7", border: "#f59e0b", text: "#92400e", headerBg: "#fcd34d" },
  { bg: "#ede9fe", border: "#8b5cf6", text: "#5b21b6", headerBg: "#c4b5fd" },
  { bg: "#fce7f3", border: "#ec4899", text: "#9d174d", headerBg: "#f9a8d4" },
  { bg: "#ccfbf1", border: "#14b8a6", text: "#115e59", headerBg: "#5eead4" },
  { bg: "#ffedd5", border: "#f97316", text: "#9a3412", headerBg: "#fdba74" },
  { bg: "#e0e7ff", border: "#6366f1", text: "#3730a3", headerBg: "#a5b4fc" },
];

function getMonthRange(
  startMonth: number,
  startYear: number,
  endMonth: number,
  endYear: number,
): { month: number; year: number }[] {
  const months: { month: number; year: number }[] = [];
  let currentMonth = startMonth;
  let currentYear = startYear;

  while (
    currentYear < endYear ||
    (currentYear === endYear && currentMonth <= endMonth)
  ) {
    months.push({ month: currentMonth, year: currentYear });
    currentMonth++;
    if (currentMonth > 12) {
      currentMonth = 1;
      currentYear++;
    }
  }
  return months;
}

function shortMonth(month: number): string {
  return MONTH_NAMES[month - 1].slice(0, 3);
}

// Edit modal
function EditCellModal({
  isOpen,
  onClose,
  onSave,
  categoryName,
  subcategoryName,
  month,
  year,
  currentValue,
  budget,
  saving,
  totalClaimedSoFar, // Total variance EXCLUDING current month
  paymentInfo, // Payment data for this cell (if any)
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (value: number) => void;
  categoryName: string;
  subcategoryName: string;
  month: number;
  year: number;
  currentValue: number;
  budget: number;
  saving: boolean;
  totalClaimedSoFar: number;
  paymentInfo?: PaymentCellInfo | null;
}) {
  const [mode, setMode] = useState<"percentage" | "amount">("percentage");
  const [value, setValue] = useState(currentValue.toString());
  const [amountValue, setAmountValue] = useState(
    ((budget * currentValue) / 100).toFixed(2),
  );

  // Sync amount when percentage changes
  const handlePercentageChange = (val: string) => {
    setValue(val);
    const pct = parseFloat(val) || 0;
    const amt = (budget * pct) / 100;
    setAmountValue(amt.toFixed(2));
  };

  // Sync percentage when amount changes
  const handleAmountChange = (val: string) => {
    setAmountValue(val);
    const amt = parseFloat(val) || 0;
    const pct = budget > 0 ? (amt / budget) * 100 : 0;
    setValue(pct.toFixed(2)); // Keep precision for calculation
  };

  const currentPercentage = parseFloat(value) || 0;
  const currentAmount = parseFloat(amountValue) || 0;

  // Calculate status
  const totalClaimedIncludingCurrent = totalClaimedSoFar + currentAmount;
  let statusColor = "bg-green-100 text-green-700";
  let statusText = "On Track";

  if (totalClaimedIncludingCurrent > budget) {
    statusColor = "bg-red-100 text-red-700";
    statusText = "Over Limit";
  } else if (totalClaimedIncludingCurrent >= budget * 0.99) {
    statusColor = "bg-blue-100 text-blue-700";
    statusText = "Max Reached";
  } else if (totalClaimedIncludingCurrent >= budget * 0.9) {
    statusColor = "bg-yellow-100 text-yellow-800";
    statusText = "Near Limit";
  }

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 24,
          width: 380,
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 18 }}>
              {MONTH_NAMES[month - 1]} {year}
            </div>
            <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
              {categoryName} <span style={{ color: "#cbd5e1" }}>/</span>{" "}
              {subcategoryName}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
            }}
          >
            <X size={20} color="#64748b" />
          </button>
        </div>

        {/* Toggle Mode */}
        <div
          style={{
            display: "flex",
            background: "#f1f5f9",
            padding: 4,
            borderRadius: 8,
            marginBottom: 20,
          }}
        >
          <button
            type="button"
            onClick={() => setMode("percentage")}
            style={{
              flex: 1,
              padding: "6px 12px",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              background: mode === "percentage" ? "#fff" : "transparent",
              color: mode === "percentage" ? "#0f172a" : "#64748b",
              boxShadow:
                mode === "percentage" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            Percentage (%)
          </button>
          <button
            type="button"
            onClick={() => setMode("amount")}
            style={{
              flex: 1,
              padding: "6px 12px",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              background: mode === "amount" ? "#fff" : "transparent",
              color: mode === "amount" ? "#0f172a" : "#64748b",
              boxShadow:
                mode === "amount" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            Amount ($)
          </button>
        </div>

        {/* Input Area */}
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {mode === "amount" && (
              <span style={{ fontSize: 24, fontWeight: 600, color: "#94a3b8" }}>
                $
              </span>
            )}
            <input data-seq-private
              type="number"
              className="form-input"
              style={{
                width: mode === "percentage" ? 120 : 180,
                fontSize: 32,
                fontWeight: 700,
                textAlign: "center",
                padding: "8px",
                border: "none",
                background: "transparent",
                outline: "none",
                color: "#0f172a",
              }}
              value={mode === "percentage" ? value : amountValue}
              onChange={(e) =>
                mode === "percentage"
                  ? handlePercentageChange(e.target.value)
                  : handleAmountChange(e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") onSave(currentPercentage);
                if (e.key === "Escape") onClose();
              }}
              autoFocus
              step={mode === "percentage" ? "0.1" : "1"}
            />
            {mode === "percentage" && (
              <span style={{ fontSize: 24, fontWeight: 600, color: "#94a3b8" }}>
                %
              </span>
            )}
          </div>
          <div data-seq-private="" style={{ color: "#64748b", fontSize: 14, marginTop: 8 }}>
            {mode === "percentage"
              ? formatCurrency(currentAmount)
              : `${currentPercentage.toFixed(2)}%`}
          </div>
        </div>

        {/* Status Card */}
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            overflow: "hidden",
            marginBottom: 24,
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 13, color: "#64748b" }}>Status</span>
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${statusColor}`}
              // Fallback styles if tailwind classes not working directly
              style={{
                background:
                  statusText === "Over Limit"
                    ? "#fef2f2"
                    : statusText === "Max Reached"
                      ? "#eff6ff"
                      : statusText === "Near Limit"
                        ? "#fefce8"
                        : "#f0fdf4",
                color:
                  statusText === "Over Limit"
                    ? "#b91c1c"
                    : statusText === "Max Reached"
                      ? "#1d4ed8"
                      : statusText === "Near Limit"
                        ? "#854d0e"
                        : "#15803d",
                padding: "2px 8px",
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {statusText}
            </span>
          </div>
          <div style={{ padding: "12px 16px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                marginBottom: 8,
              }}
            >
              <span style={{ color: "#64748b" }}>Contract Value</span>
              <span data-seq-private="" style={{ fontWeight: 500 }}>{formatCurrency(budget)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
              }}
            >
              <span style={{ color: "#64748b" }}>Total Claimed</span>
              <span data-seq-private=""
                style={{
                  fontWeight: 600,
                  color:
                    totalClaimedIncludingCurrent > budget
                      ? "#ef4444"
                      : "#0f172a",
                }}
              >
                {formatCurrency(totalClaimedIncludingCurrent)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment History Section */}
        {(() => {
          // Pre-calculate payment metrics for display
          const hasPmt = paymentInfo && paymentInfo.action !== "pending";
          // Use CURRENT modal values (what user is editing), not stale payment record
          const currentClaimPct = parseFloat(value) || 0;
          const currentClaimAmt = parseFloat(amountValue) || 0;
          const paidPctOfBudget = hasPmt && budget > 0
            ? Math.round((paymentInfo.paid_amount / budget) * 100 * 100) / 100
            : 0;
          const claimedPctOfBudget = currentClaimPct;
          const remainingAmt = hasPmt
            ? Math.max(0, currentClaimAmt - paymentInfo.paid_amount)
            : 0;
          const overpaidAmt = hasPmt
            ? Math.max(0, paymentInfo.paid_amount - currentClaimAmt)
            : 0;
          const isOverpaid = overpaidAmt > 0.01 && currentClaimAmt > 0;
          // Visual bar: how much of current claimed was actually paid (0-100%)
          const paidOfClaimedRatio = hasPmt && currentClaimAmt > 0
            ? Math.min(100, (paymentInfo.paid_amount / currentClaimAmt) * 100)
            : 0;

          return (
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                overflow: "hidden",
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  padding: "10px 16px",
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>💰 Payment</span>
                {hasPmt ? (
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      background: getActionColor(paymentInfo.action).bg,
                      color: getActionColor(paymentInfo.action).text,
                      border: `1px solid ${getActionColor(paymentInfo.action).border}`,
                    }}
                  >
                    {getActionLabel(paymentInfo.action)}
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>No payment</span>
                )}
              </div>
              <div style={{ padding: "12px 16px" }}>
                {hasPmt ? (
                  <>
                    {/* Visual bar: Claimed vs Paid */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 11,
                        color: "#94a3b8",
                        marginBottom: 4,
                      }}>
                        <span>Paid vs Claimed</span>
                        <span>
                          {paymentInfo.action === "voided"
                            ? "Voided"
                            : `${formatCurrency(paymentInfo.paid_amount)} of ${formatCurrency(currentClaimAmt)}`}
                        </span>
                      </div>
                      {/* Stacked bar */}
                      <div style={{
                        height: 8,
                        borderRadius: 4,
                        background: "#f1f5f9",
                        overflow: "hidden",
                        position: "relative",
                      }}>
                        {/* Claimed extent (full bar background) */}
                        <div style={{
                          position: "absolute",
                          top: 0, left: 0, bottom: 0,
                          width: `${Math.min(100, claimedPctOfBudget)}%`,
                          background: "rgba(234, 179, 8, 0.25)",
                          borderRadius: 4,
                        }} />
                        {/* Paid portion */}
                        <div style={{
                          position: "absolute",
                          top: 0, left: 0, bottom: 0,
                          width: `${Math.min(100, paidPctOfBudget)}%`,
                          background: paymentInfo.action === "voided"
                            ? "#fca5a5"
                            : paidOfClaimedRatio >= 99.9 ? "#4ade80" : "#f59e0b",
                          borderRadius: 4,
                          transition: "width 0.3s ease",
                        }} />
                      </div>
                      {/* Bar legend */}
                      <div style={{
                        display: "flex",
                        gap: 12,
                        marginTop: 4,
                        fontSize: 10,
                        color: "#94a3b8",
                      }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: "rgba(234, 179, 8, 0.4)" }} />
                          Claimed {claimedPctOfBudget}%
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: paidOfClaimedRatio >= 99.9 ? "#4ade80" : "#f59e0b" }} />
                          Paid {paidPctOfBudget}%
                        </span>
                      </div>
                    </div>

                    {/* Key numbers: $ focused, not % */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      marginBottom: paymentInfo.action === "voided" || paymentInfo.action === "works_not_completed" ? 0 : 0,
                    }}>
                      {/* Paid */}
                      <div style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        background: paymentInfo.action === "voided" ? "#fef2f2" : "#f0fdf4",
                        textAlign: "center",
                      }}>
                        <div style={{ fontSize: 10, color: paymentInfo.action === "voided" ? "#dc2626" : "#16a34a", fontWeight: 500, marginBottom: 2 }}>
                          {paymentInfo.action === "voided" ? "Voided" : "Paid"}
                        </div>
                        <div data-seq-private="" style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: paymentInfo.action === "voided" ? "#dc2626" : "#16a34a",
                        }}>
                          {paymentInfo.action === "voided"
                            ? "$0"
                            : formatCurrency(paymentInfo.paid_amount)}
                        </div>
                      </div>
                      {/* Remaining / Overpaid */}
                      <div style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        background: isOverpaid ? "#fef2f2" : remainingAmt > 0 ? "#fff7ed" : "#f0fdf4",
                        textAlign: "center",
                      }}>
                        <div style={{
                          fontSize: 10,
                          color: isOverpaid ? "#dc2626" : remainingAmt > 0 ? "#ea580c" : "#16a34a",
                          fontWeight: 500,
                          marginBottom: 2,
                        }}>
                          {isOverpaid ? "⚠ Overpaid" : "Remaining"}
                        </div>
                        <div data-seq-private="" style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: isOverpaid ? "#dc2626" : remainingAmt > 0 ? "#ea580c" : "#16a34a",
                        }}>
                          {isOverpaid ? `+${formatCurrency(overpaidAmt)}` : formatCurrency(remainingAmt)}
                        </div>
                      </div>
                    </div>

                    {/* Contextual action banners */}
                    {isOverpaid && (
                      <div data-seq-private="" style={{
                        marginTop: 8,
                        padding: "6px 10px",
                        background: "#fef2f2",
                        border: "1px solid #fca5a5",
                        borderRadius: 6,
                        fontSize: 11,
                        color: "#dc2626",
                        fontWeight: 500,
                      }}>
                        ⚠️ Paid exceeds current claim by {formatCurrency(overpaidAmt)}. Adjust payment or increase claim
                      </div>
                    )}
                    {remainingAmt > 0 && !isOverpaid && paymentInfo.action !== "voided" && (
                      <div data-seq-private="" style={{
                        marginTop: 8,
                        padding: "6px 10px",
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        borderRadius: 6,
                        fontSize: 11,
                        color: "#92400e",
                        fontWeight: 500,
                      }}>
                        💡 {formatCurrency(remainingAmt)} unpaid, reclaim in next month
                      </div>
                    )}
                    {paymentInfo.action === "works_not_completed" && (
                      <div style={{
                        marginTop: 8,
                        padding: "6px 10px",
                        background: "#fff7ed",
                        border: "1px solid #fed7aa",
                        borderRadius: 6,
                        fontSize: 11,
                        color: "#ea580c",
                        fontWeight: 500,
                      }}>
                        ⏭ Pushed to next month for reclaiming
                      </div>
                    )}
                    {paymentInfo.action === "voided" && (
                      <div style={{
                        marginTop: 8,
                        padding: "6px 10px",
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 6,
                        fontSize: 11,
                        color: "#dc2626",
                        fontWeight: 500,
                      }}>
                        🚫 Claim voided, balance zeroed out
                      </div>
                    )}
                    {paymentInfo.agreed_amount != null && paymentInfo.action === "agreement_made" && (
                      <div data-seq-private="" style={{
                        marginTop: 8,
                        padding: "6px 10px",
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        borderRadius: 6,
                        fontSize: 11,
                        color: "#1e40af",
                        fontWeight: 500,
                      }}>
                        ✎ Agreed amount: {formatCurrency(paymentInfo.agreed_amount)}
                      </div>
                    )}
                    {paymentInfo.notes && (
                      <div style={{ marginTop: 8, fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
                        📝 {paymentInfo.notes}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: "8px 0" }}>
                    No payment recorded yet.
                    <br />
                    <span style={{ fontSize: 11 }}>Use Claim Reports → Add Payment.</span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Actions */}
        <div style={{ display: "flex", gap: 12 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            style={{ flex: 1, height: 44 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSave(currentPercentage)}
            disabled={saving}
            style={{ flex: 1, height: 44 }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ProgressRangeView({
  project,
  progress,
  onProgressUpdate,
  highlightSubcategoryId,
  highlightCategoryId,
}: ProgressRangeViewProps) {
  const { addToast } = useToast();
  const tour = useTourManager('progress_claims');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingProgrammatically = useRef(false);
  const currentYear = new Date().getFullYear();
  const highlightRef = useRef<HTMLTableRowElement>(null);
  const highlightCategoryRef = useRef<HTMLTableRowElement>(null);

  // Auto-scroll to highlighted subcategory from deep link
  useEffect(() => {
    if (highlightSubcategoryId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightSubcategoryId]);

  // Auto-scroll to highlighted category from notes mention deep link
  useEffect(() => {
    if (highlightCategoryId && highlightCategoryRef.current) {
      highlightCategoryRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightCategoryId]);

  const [startMonth, setStartMonth] = useState(1);
  const [startYear, setStartYear] = useState(currentYear);
  const [endMonth, setEndMonth] = useState(12);
  const [endYear, setEndYear] = useState(currentYear);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [subcategoryTargets, setSubcategoryTargets] = useState<Record<string, { target_type: string; target_value: number; trigger_mode: string; message: string }>>({}); // subcategoryId -> target config
  const [categoryTargets, setCategoryTargets] = useState<Record<string, { target_type: string; target_value: number; trigger_mode: string; message: string; included_subcategory_ids: string[] }>>({}); // categoryId -> target config
  const [hoveredTarget, setHoveredTarget] = useState<{ id: string; rect: DOMRect; type: 'sub' | 'cat' } | null>(null);
  const [targetNotification, setTargetNotification] = useState<TargetNotification | null>(null);
  const [editModal, setEditModal] = useState<{
    subcategoryId: string;
    categoryName: string;
    subcategoryName: string;
    month: number;
    year: number;
    budget: number;
    totalClaimedSoFar: number;
    paymentInfo?: PaymentCellInfo | null;
  } | null>(null);

  // Payment cell map for split-cell rendering
  const [paymentCellMap, setPaymentCellMap] = useState<Map<string, PaymentCellInfo>>(new Map());

  // SEQ-247: a failed payment read is shown, not swallowed. `paymentRetry`
  // re-runs the fetch from the Try again button.
  const [paymentLoadError, setPaymentLoadError] = useState(false);
  const [paymentRetry, setPaymentRetry] = useState(0);
  // Retry feedback: the button shows Retrying while the read runs, a second
  // failure says so, and focus moves somewhere real when the button unmounts.
  // One phase, set only by the Try again click and the read it starts, so an
  // ordinary refetch after an edit never moves focus or changes the message.
  const [retryPhase, setRetryPhase] = useState<"idle" | "retrying" | "failedAgain" | "recovered">("idle");
  const paymentRetryBtnRef = useRef<HTMLButtonElement>(null);
  const paymentRecoveredRef = useRef<HTMLParagraphElement>(null);

  // Fetch payment line items for split-cell overlay
  useEffect(() => {
    // A late answer from an older fetch must not overwrite a newer one.
    let cancelled = false;
    const fetchPaymentData = async () => {
      try {
        // A read that never answers must not leave Retrying on screen for ever.
        let timer: ReturnType<typeof setTimeout> | undefined;
        const lineItems = await Promise.race([
          fetchPaymentLineItemsByProject(project.id),
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new Error("payment read timed out")), 20000);
          }),
        ]).finally(() => clearTimeout(timer));
        if (cancelled) return;
        const map = buildPaymentCellMap(lineItems);
        setPaymentCellMap(map);
        setPaymentLoadError(false);
        setRetryPhase((ph) => (ph === "retrying" ? "recovered" : "idle"));
      } catch (err) {
        logError("[ProgressRangeView] Failed to fetch payment line items:", err);
        if (!cancelled) {
          // Drop any earlier map so stale paid amounts are not shown as current.
          setPaymentCellMap(new Map());
          setPaymentLoadError(true);
          setRetryPhase((ph) => (ph === "retrying" || ph === "failedAgain" ? "failedAgain" : "idle"));
        }
      }
    };
    fetchPaymentData();
    return () => {
      cancelled = true;
    };
  }, [project.id, progress, paymentRetry]); // Re-fetch when progress changes (payment may have been updated)

  // After a retry settles, put keyboard focus somewhere real: the button
  // again if it failed, the status line if it worked (the button is gone).
  useEffect(() => {
    if (retryPhase === "recovered") paymentRecoveredRef.current?.focus();
    else if (retryPhase === "failedAgain") paymentRetryBtnRef.current?.focus();
  }, [retryPhase]);

  const monthColumns = getMonthRange(startMonth, startYear, endMonth, endYear);

  // Build grouped data
  const groupedData = project.categories.map((cat, catIdx) => {
    const color = CATEGORY_COLORS[catIdx % CATEGORY_COLORS.length];
    if (cat.is_custom_amount_only) {
      const fixedSub = cat.subcategories.find(
        (s) => s.name.toUpperCase() === "FIXED AMOUNT",
      );
      return {
        categoryName: cat.name,
        categoryId: cat.id,
        color,
        rows: [
          {
            subcategoryName: "Fixed Amount",
            subcategoryId: fixedSub ? fixedSub.id : `FIXED:${cat.id}`,
            budget: Number(cat.custom_amount) || 0,
          },
        ],
      };
    }
    return {
      categoryName: cat.name,
      categoryId: cat.id,
      color,
      rows: cat.subcategories.map((sub) => ({
        subcategoryName: sub.name,
        subcategoryId: sub.id,
        budget: Number(sub.amount) || 0,
      })),
    };
  });

  // Fetch subcategory targets on mount
  useEffect(() => {
    const allSubIds = project.categories.flatMap((cat) =>
      (cat.subcategories || []).map((s) => s.id),
    );
    if (allSubIds.length === 0) return;

    const fetchTargets = async () => {
       
      const { data } = await (supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic table not in generated Supabase types
        .from("claims_subcategory_targets") as any)
        .select("subcategory_id, is_enabled, target_type, target_value, trigger_mode, message")
        .in("subcategory_id", allSubIds)
        .eq("is_enabled", true);

      if (data) {
        const map: Record<string, { target_type: string; target_value: number; trigger_mode: string; message: string }> = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data.forEach((t: any) => {
          map[t.subcategory_id] = {
            target_type: t.target_type,
            target_value: Number(t.target_value),
            trigger_mode: t.trigger_mode,
            message: t.message || "",
          };
        });
        setSubcategoryTargets(map);
      }
    };
    fetchTargets();
  }, [project.categories]);

  // Fetch category targets on mount
  useEffect(() => {
    const allCatIds = project.categories.map((cat) => cat.id);
    if (allCatIds.length === 0) return;

    const fetchCategoryTargets = async () => {
       
      const { data } = await (supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic table not in generated Supabase types
        .from("claims_category_targets") as any)
        .select("category_id, is_enabled, target_type, target_value, trigger_mode, message, included_subcategory_ids")
        .in("category_id", allCatIds)
        .eq("is_enabled", true);

      if (data) {
        const map: Record<string, { target_type: string; target_value: number; trigger_mode: string; message: string; included_subcategory_ids: string[] }> = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data.forEach((t: any) => {
          map[t.category_id] = {
            target_type: t.target_type,
            target_value: Number(t.target_value),
            trigger_mode: t.trigger_mode,
            message: t.message || "",
            included_subcategory_ids: t.included_subcategory_ids || [],
          };
        });
        setCategoryTargets(map);
      }
    };
    fetchCategoryTargets();
  }, [project.categories]);

  // Track active category on scroll
  const handleScroll = useCallback(() => {
    // Skip if we're doing a programmatic scroll
    if (isScrollingProgrammatically.current) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    let foundCategory: string | null = null;
    const containerRect = container.getBoundingClientRect();

    // Find which category's first subcategory row is visible at the top
    // This is more reliable than checking sticky headers
    const firstSubRows = container.querySelectorAll("[data-first-sub-row]");
    for (const row of firstSubRows) {
      const rect = row.getBoundingClientRect();
      const categoryId = row.getAttribute("data-first-sub-row");
      // Check if this row is at or below the visible header area
      // The first one that's still visible in the table is our active category
      if (
        rect.top <= containerRect.top + 120 &&
        rect.bottom > containerRect.top
      ) {
        foundCategory = categoryId;
      }
    }

    if (foundCategory && foundCategory !== activeCategory) {
      setActiveCategory(foundCategory);
    }
  }, [activeCategory]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    // Initial check
    handleScroll();

    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollLeft = () => {
    if (scrollContainerRef.current)
      scrollContainerRef.current.scrollLeft -= 200;
  };
  const scrollRight = () => {
    if (scrollContainerRef.current)
      scrollContainerRef.current.scrollLeft += 200;
  };

  const getProgressValue = (
    subcategoryId: string,
    month: number,
    year: number,
  ) => {
    return progress.find(
      (p) =>
        p.subcategory_id === subcategoryId &&
        p.claim_month === month &&
        p.claim_year === year,
    );
  };

  const getTotalPercentage = (subcategoryId: string): number => {
    return progress
      .filter((p) => p.subcategory_id === subcategoryId)
      .reduce((sum, p) => sum + Number(p.percentage || 0), 0);
  };

  const getTotalAmount = (subcategoryId: string, budget: number): number => {
    return (budget * getTotalPercentage(subcategoryId)) / 100;
  };

  const handleSaveProgress = async (
    subcategoryId: string,
    month: number,
    year: number,
    percentage: number,
    budget: number,
  ) => {
    setSaving(true);
    try {
      // Check if this is a fallback fixed amount ID that needs subcategory creation
      let actualSubcategoryId = subcategoryId;
      if (subcategoryId.startsWith("FIXED:")) {
        const categoryId = subcategoryId.replace("FIXED:", "");

        // IMPORTANT: First check if a FIXED AMOUNT subcategory already exists
        const { data: existingSub } = await supabase
          .from("claims_subcategories")
          .select("id")
          .eq("category_id", categoryId)
          .eq("name", "FIXED AMOUNT")
          .limit(1)
          .single();

        if (existingSub) {
          // Use the existing subcategory
          actualSubcategoryId = existingSub.id;
        } else {
          // Create the missing FIXED AMOUNT subcategory only if it doesn't exist
          const { data: newSub, error: createError } = await supabase
            .from("claims_subcategories")
            .insert({
              organization_id: requireOrganizationId(),
              name: "FIXED AMOUNT",
              amount: budget,
              category_id: categoryId,
              sort_order: 0,
            })
            .select()
            .single();

          if (createError) {
            logError("Failed to create fixed amount subcategory:", createError);
            throw new Error(
              "Failed to create subcategory for fixed amount category",
            );
          }

          actualSubcategoryId = newSub.id;
        }
      }

      const amount = (budget * percentage) / 100;
      const existingProgress = progress.find(
        (p) =>
          p.subcategory_id === actualSubcategoryId &&
          p.claim_month === month &&
          p.claim_year === year,
      );

      if (existingProgress) {
        const { error } = await supabase
          .from("claims_progress")
          .update({ percentage, amount, input_type: "percentage" })
          .eq("id", existingProgress.id);
        if (error) throw error;
        onProgressUpdate(
          progress.map((p) =>
            p.id === existingProgress.id ? { ...p, percentage, amount } : p,
          ),
        );
      } else if (percentage > 0) {
        const { data, error } = await supabase
          .from("claims_progress")
          .insert({
            organization_id: requireOrganizationId(),
            subcategory_id: actualSubcategoryId,
            claim_month: month,
            claim_year: year,
            percentage,
            amount,
            input_type: "percentage",
          })
          .select()
          .single();
        if (error) throw error;
        onProgressUpdate([...progress, data]);
      }

      setEditModal(null);
      addToast({
        type: "success",
        title: "Saved",
        message: `${MONTH_NAMES[month - 1]} ${year} updated`,
      });

      // ─── Fire-and-forget: check subcategory target notification ───
      // Calculate cumulative progress across ALL months for this subcategory
      // We need the updated progress array to include the just-saved value
      const updatedProgress = existingProgress
        ? progress.map((p) =>
            p.id === existingProgress.id ? { ...p, percentage, amount } : p,
          )
        : [...progress, { subcategory_id: actualSubcategoryId, percentage, amount } as Partial<ClaimsProgress> as ClaimsProgress];

      const cumulativePercentage = updatedProgress
        .filter((p) => p.subcategory_id === actualSubcategoryId)
        .reduce((sum, p) => sum + Number(p.percentage || 0), 0);

      const cumulativeAmount = (budget * cumulativePercentage) / 100;

      supabase.functions
        .invoke("notify-target-met", {
          body: {
            subcategoryId: actualSubcategoryId,
            currentPercentage: cumulativePercentage,
            currentAmount: cumulativeAmount,
            budget,
            originUrl: window.location.origin,
            localTime: new Date().toLocaleString("en-AU", {
              month: "long",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            }),
          },
        })
        .then(({ data, error: fnErr }) => {
          if (fnErr) {
            logWarn("Target notification check failed:", fnErr);
            return;
          }
          // Show in-app popup if a target was actually triggered
          if (data?.notified && data?.notification) {
            setTargetNotification(data.notification as TargetNotification);
          }
        })
        .catch((err) => logWarn("Target notification error:", err));
    } catch (err) {
      logError("Error saving:", err);
      addToast({
        type: "error",
        title: "Save Failed",
        message: err instanceof Error ? err.message : "Could not save progress",
      });
    } finally {
      setSaving(false);
    }
  };

  const setYearPreset = (year: number) => {
    setStartMonth(1);
    setStartYear(year);
    setEndMonth(12);
    setEndYear(year);
  };

  // Frozen column widths
  const COL_NAME = 260; // Increased from 200 to fit longer category names
  const COL_BUDGET = 80;
  const COL_TOTAL = 60;
  const COL_CLAIMED = 75;

  return (
    <>
      {/* Controls */}
      <div
        data-tour-id="pc-year-controls"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", gap: 4 }}>
          {[
            currentYear - 3,
            currentYear - 2,
            currentYear - 1,
            currentYear,
            currentYear + 1,
          ].map((year) => (
            <button
              key={year}
              type="button"
              className={`btn btn-sm ${
                startYear === year &&
                endYear === year &&
                startMonth === 1 &&
                endMonth === 12
                  ? "btn-primary"
                  : "btn-secondary"
              }`}
              onClick={() => setYearPreset(year)}
            >
              {year}
            </button>
          ))}
        </div>
        <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
          }}
        >
          <select
            className="form-input-sm"
            value={startMonth}
            onChange={(e) => setStartMonth(parseInt(e.target.value))}
          >
            {MONTH_NAMES.map((name, i) => (
              <option key={i} value={i + 1}>
                {name.slice(0, 3)}
              </option>
            ))}
          </select>
          <select
            className="form-input-sm"
            style={{ width: 78 }}
            value={startYear}
            onChange={(e) => setStartYear(parseInt(e.target.value))}
          >
            {[
              currentYear - 3,
              currentYear - 2,
              currentYear - 1,
              currentYear,
              currentYear + 1,
              currentYear + 2,
            ].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <span style={{ color: "#64748b" }}>→</span>
          <select
            className="form-input-sm"
            value={endMonth}
            onChange={(e) => setEndMonth(parseInt(e.target.value))}
          >
            {MONTH_NAMES.map((name, i) => (
              <option key={i} value={i + 1}>
                {name.slice(0, 3)}
              </option>
            ))}
          </select>
          <select
            className="form-input-sm"
            style={{ width: 78 }}
            value={endYear}
            onChange={(e) => setEndYear(parseInt(e.target.value))}
          >
            {[
              currentYear - 3,
              currentYear - 2,
              currentYear - 1,
              currentYear,
              currentYear + 1,
              currentYear + 2,
            ].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={scrollLeft}
            style={{ padding: "4px 8px" }}
          >
            <ChevronLeft size={16} />
          </button>
          <span
            style={{
              fontSize: 12,
              color: "#64748b",
              minWidth: 60,
              textAlign: "center",
            }}
          >
            {monthColumns.length} months
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={scrollRight}
            style={{ padding: "4px 8px" }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {groupedData.length === 0 && (
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text-secondary)" }}>
          This project has no claim categories yet. Open the Overview tab and choose Edit
          Categories to add them, then come back to track progress by month.
        </p>
      )}

      {/* Table */}
      <div
        ref={scrollContainerRef}
        style={{
          overflow: "auto",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          background: "#fff",
          maxHeight: "calc(100vh - 280px)", // Increased height
        }}
      >
        <table
          style={{
            borderCollapse: "collapse",
            fontSize: 12,
            whiteSpace: "nowrap",
          }}
        >
          <thead>
            <tr
              style={{
                position: "sticky",
                top: 0,
                zIndex: 30,
                background: "#f8fafc",
              }}
            >
              <th
                style={{
                  position: "sticky",
                  left: 0,
                  zIndex: 35,
                  background: "#f8fafc",
                  padding: "10px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #e2e8f0",
                  width: COL_NAME,
                  minWidth: COL_NAME,
                }}
              >
                Name
              </th>
              <th
                style={{
                  position: "sticky",
                  left: COL_NAME,
                  zIndex: 35,
                  background: "#f8fafc",
                  padding: "10px 8px",
                  textAlign: "right",
                  borderBottom: "2px solid #e2e8f0",
                  width: COL_BUDGET,
                  minWidth: COL_BUDGET,
                }}
              >
                Contract Value
              </th>
              <th
                style={{
                  position: "sticky",
                  left: COL_NAME + COL_BUDGET,
                  zIndex: 35,
                  background: "#f8fafc",
                  padding: "10px 8px",
                  textAlign: "right",
                  borderBottom: "2px solid #e2e8f0",
                  width: COL_TOTAL,
                  minWidth: COL_TOTAL,
                }}
              >
                Total %
              </th>
              <th
                style={{
                  position: "sticky",
                  left: COL_NAME + COL_BUDGET + COL_TOTAL,
                  zIndex: 35,
                  background: "#f8fafc",
                  padding: "10px 8px",
                  textAlign: "right",
                  borderBottom: "2px solid #e2e8f0",
                  borderRight: "2px solid #cbd5e1",
                  width: COL_CLAIMED,
                  minWidth: COL_CLAIMED,
                }}
              >
                Claimed
              </th>
              {monthColumns.map(({ month, year }) => {
                // Calculate total for this month across all subcategories
                const monthTotal = progress
                  .filter(
                    (p) => p.claim_month === month && p.claim_year === year,
                  )
                  .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
                return (
                  <th
                    key={`${month}-${year}`}
                    style={{
                      padding: "8px 6px",
                      textAlign: "center",
                      background: "#f8fafc",
                      borderBottom: "2px solid #e2e8f0",
                      minWidth: 65,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{shortMonth(month)}</div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "#94a3b8",
                        fontWeight: 400,
                      }}
                    >
                      {year}
                    </div>
                    <div data-seq-private=""
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: monthTotal > 0 ? "#3b82f6" : "#cbd5e1",
                        marginTop: 2,
                      }}
                    >
                      {monthTotal > 0 ? formatCurrency(monthTotal) : "—"}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {groupedData.map((group) => (
              <Fragment key={group.categoryId}>
                {/* Category Header - Sticky both ways */}
                <tr
                  key={`cat-${group.categoryId}`}
                  data-category-id={group.categoryId}
                  {...(group === groupedData[0] ? { 'data-tour-id': 'pc-category-row' } : {})}
                  style={{ position: "sticky", top: 44, zIndex: 20 }}
                >
                  <td
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 25,
                      background: group.color.headerBg,
                      borderLeft: `4px solid ${group.color.border}`,
                      padding: "10px 12px",
                      fontWeight: 700,
                      fontSize: 13,
                      color: group.color.text,
                      borderBottom: `2px solid ${group.color.border}`,
                      width: COL_NAME,
                      minWidth: COL_NAME,
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      {group.categoryName}
                      {categoryTargets[group.categoryId] && (() => {
                        const catTarget = categoryTargets[group.categoryId];
                        const isHovered = hoveredTarget?.id === group.categoryId && hoveredTarget?.type === 'cat';
                        return (
                          <span
                            style={{ position: "relative", display: "inline-flex" }}
                            onMouseEnter={(e) => {
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setHoveredTarget({ id: group.categoryId, rect, type: 'cat' });
                            }}
                            onMouseLeave={() => setHoveredTarget(null)}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 2,
                                padding: "2px 6px",
                                borderRadius: 8,
                                background: isHovered ? "rgba(245, 158, 11, 0.3)" : "rgba(245, 158, 11, 0.18)",
                                color: "#d97706",
                                fontSize: 9,
                                fontWeight: 600,
                                lineHeight: 1,
                                cursor: "default",
                                transition: "background 0.15s ease",
                              }}
                            >
                              <Crosshair size={10} />
                            </span>
                            {/* Category target tooltip via portal */}
                            {isHovered && hoveredTarget && createPortal(
                              <div
                                style={{
                                  position: "fixed",
                                  top: hoveredTarget.rect.bottom + 6,
                                  left: hoveredTarget.rect.left,
                                  zIndex: 99999,
                                  minWidth: 250,
                                  maxWidth: 340,
                                  background: "#1e293b",
                                  color: "#f1f5f9",
                                  borderRadius: 10,
                                  padding: "12px 14px",
                                  boxShadow: "0 8px 24px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.15)",
                                  fontSize: 11,
                                  lineHeight: 1.5,
                                  animation: "fadeIn 0.15s ease",
                                  pointerEvents: "none",
                                }}
                              >
                                {/* Arrow */}
                                <div style={{
                                  position: "absolute",
                                  top: -5,
                                  left: 12,
                                  width: 10,
                                  height: 10,
                                  background: "#1e293b",
                                  transform: "rotate(45deg)",
                                  borderRadius: 2,
                                }} />
                                {/* Header */}
                                <div style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                  marginBottom: 8,
                                  paddingBottom: 7,
                                  borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
                                }}>
                                  <Crosshair size={12} style={{ color: "#fbbf24" }} />
                                  <span style={{ fontWeight: 700, fontSize: 12, color: "#fbbf24" }}>Category Target</span>
                                </div>
                                {/* Details */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span style={{ color: "#94a3b8" }}>Type</span>
                                    <span style={{ fontWeight: 600, textTransform: "capitalize" }}>
                                      {catTarget.target_type}
                                    </span>
                                  </div>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span style={{ color: "#94a3b8" }}>Threshold</span>
                                    <span style={{ fontWeight: 600, color: "#fbbf24" }}>
                                      {catTarget.target_type === "percentage"
                                        ? `${catTarget.target_value}%`
                                        : `$${catTarget.target_value.toLocaleString()}`}
                                    </span>
                                  </div>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span style={{ color: "#94a3b8" }}>Trigger</span>
                                    <span style={{
                                      fontWeight: 600,
                                      padding: "1px 6px",
                                      borderRadius: 6,
                                      fontSize: 10,
                                      background: catTarget.trigger_mode === "once" ? "rgba(59, 130, 246, 0.2)" : "rgba(168, 85, 247, 0.2)",
                                      color: catTarget.trigger_mode === "once" ? "#93c5fd" : "#c4b5fd",
                                    }}>
                                      {catTarget.trigger_mode === "once" ? "Once only" : "Every time"}
                                    </span>
                                  </div>
                                  {/* Scope: included subcategories */}
                                  {catTarget.included_subcategory_ids.length > 0 && (() => {
                                    const cat = project.categories.find(c => c.id === group.categoryId);
                                    const subNames = catTarget.included_subcategory_ids
                                      .map(sid => cat?.subcategories.find(s => s.id === sid)?.name)
                                      .filter(Boolean);
                                    const totalSubs = cat?.subcategories.length || 0;
                                    const isAll = subNames.length === totalSubs;
                                    return (
                                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <span style={{ color: "#94a3b8" }}>Scope</span>
                                        <span style={{ fontWeight: 600, textAlign: "right", maxWidth: 160 }}>
                                          {isAll ? "All subcategories" : subNames.join(", ")}
                                        </span>
                                      </div>
                                    );
                                  })()}
                                  {catTarget.included_subcategory_ids.length === 0 && (
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                      <span style={{ color: "#94a3b8" }}>Scope</span>
                                      <span style={{ fontWeight: 600 }}>Fixed amount</span>
                                    </div>
                                  )}
                                </div>
                                {/* Message */}
                                {catTarget.message && (
                                  <div style={{
                                    marginTop: 8,
                                    paddingTop: 7,
                                    borderTop: "1px solid rgba(148, 163, 184, 0.2)",
                                  }}>
                                    <div style={{ color: "#94a3b8", fontSize: 10, marginBottom: 3 }}>Message</div>
                                    <div style={{
                                      fontSize: 11,
                                      color: "#e2e8f0",
                                      fontStyle: "italic",
                                      lineHeight: 1.4,
                                      wordBreak: "break-word",
                                    }}>
                                      "{catTarget.message}"
                                    </div>
                                  </div>
                                )}
                              </div>,
                              document.body
                            )}
                          </span>
                        );
                      })()}
                    </span>
                  </td>
                  <td
                    style={{
                      position: "sticky",
                      left: COL_NAME,
                      zIndex: 25,
                      background: group.color.headerBg,
                      borderBottom: `2px solid ${group.color.border}`,
                      width: COL_BUDGET,
                      minWidth: COL_BUDGET,
                    }}
                  ></td>
                  <td
                    style={{
                      position: "sticky",
                      left: COL_NAME + COL_BUDGET,
                      zIndex: 25,
                      background: group.color.headerBg,
                      borderBottom: `2px solid ${group.color.border}`,
                      width: COL_TOTAL,
                      minWidth: COL_TOTAL,
                    }}
                  ></td>
                  <td
                    style={{
                      position: "sticky",
                      left: COL_NAME + COL_BUDGET + COL_TOTAL,
                      zIndex: 25,
                      background: group.color.headerBg,
                      borderBottom: `2px solid ${group.color.border}`,
                      borderRight: "2px solid #cbd5e1",
                      width: COL_CLAIMED,
                      minWidth: COL_CLAIMED,
                    }}
                  ></td>
                  {monthColumns.map(({ month, year }) => (
                    <td
                      key={`${month}-${year}`}
                      style={{
                        background: group.color.headerBg,
                        borderBottom: `2px solid ${group.color.border}`,
                      }}
                    ></td>
                  ))}
                </tr>

                {/* Rows */}
                {group.rows.map((row, idx) => {
                  const totalPct = getTotalPercentage(row.subcategoryId);
                  const totalAmt = getTotalAmount(
                    row.subcategoryId,
                    row.budget,
                  );
                  const isOverBudget = totalPct > 100;
                  const isNearLimit = totalPct > 90 && totalPct < 100; // 100% is green (on budget)

                  const isHighlighted = highlightSubcategoryId === row.subcategoryId;
                  const isCategoryHighlighted = highlightCategoryId === group.categoryId;
                  const isFirstRowCategoryHighlight = idx === 0 && isCategoryHighlighted;

                  // Determine ref: subcategory highlight takes priority, then category highlight on first row
                  const rowRef = isHighlighted
                    ? highlightRef
                    : isFirstRowCategoryHighlight
                      ? highlightCategoryRef
                      : undefined;

                  return (
                    <tr
                      key={row.subcategoryId}
                      ref={rowRef}
                      data-first-sub-row={
                        idx === 0 ? group.categoryId : undefined
                      }
                      style={{
                        background: isHighlighted || isCategoryHighlighted
                          ? "rgba(245, 158, 11, 0.15)"
                          : group.color.bg,
                        borderBottom: "1px solid #e2e8f0",
                        outline: isHighlighted || isCategoryHighlighted
                          ? "2px solid rgba(245, 158, 11, 0.6)"
                          : undefined,
                        outlineOffset: isHighlighted || isCategoryHighlighted ? "-2px" : undefined,
                        transition: "background 2s ease, outline 2s ease",
                      }}
                    >
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 10,
                          background: group.color.bg,
                          padding: "8px 12px",
                          fontSize: 12,
                          borderLeft: `3px solid ${group.color.border}`,
                          width: COL_NAME,
                          minWidth: COL_NAME,
                        }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {row.subcategoryName}
                          {subcategoryTargets[row.subcategoryId] && (() => {
                            const target = subcategoryTargets[row.subcategoryId];
                            const isHovered = hoveredTarget?.id === row.subcategoryId && hoveredTarget?.type === 'sub';
                            return (
                              <span
                                style={{ position: "relative", display: "inline-flex" }}
                                onMouseEnter={(e) => {
                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  setHoveredTarget({ id: row.subcategoryId, rect, type: 'sub' });
                                }}
                                onMouseLeave={() => setHoveredTarget(null)}
                              >
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 2,
                                    padding: "1px 5px",
                                    borderRadius: 8,
                                    background: isHovered ? "rgba(245, 158, 11, 0.25)" : "rgba(245, 158, 11, 0.15)",
                                    color: "#d97706",
                                    fontSize: 9,
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    cursor: "default",
                                    transition: "background 0.15s ease",
                                  }}
                                >
                                  <Crosshair size={9} />
                                </span>
                                {/* Rich tooltip via portal */}
                                {isHovered && hoveredTarget && createPortal(
                                  <div
                                    style={{
                                      position: "fixed",
                                      top: hoveredTarget.rect.bottom + 6,
                                      left: hoveredTarget.rect.left,
                                      zIndex: 99999,
                                      minWidth: 240,
                                      maxWidth: 320,
                                      background: "#1e293b",
                                      color: "#f1f5f9",
                                      borderRadius: 10,
                                      padding: "12px 14px",
                                      boxShadow: "0 8px 24px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.15)",
                                      fontSize: 11,
                                      lineHeight: 1.5,
                                      animation: "fadeIn 0.15s ease",
                                      pointerEvents: "none",
                                    }}
                                  >
                                    {/* Arrow */}
                                    <div style={{
                                      position: "absolute",
                                      top: -5,
                                      left: 12,
                                      width: 10,
                                      height: 10,
                                      background: "#1e293b",
                                      transform: "rotate(45deg)",
                                      borderRadius: 2,
                                    }} />
                                    {/* Header */}
                                    <div style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 5,
                                      marginBottom: 8,
                                      paddingBottom: 7,
                                      borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
                                    }}>
                                      <Crosshair size={12} style={{ color: "#fbbf24" }} />
                                      <span style={{ fontWeight: 700, fontSize: 12, color: "#fbbf24" }}>Target Notification</span>
                                    </div>
                                    {/* Details */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ color: "#94a3b8" }}>Type</span>
                                        <span style={{ fontWeight: 600, textTransform: "capitalize" }}>
                                          {target.target_type}
                                        </span>
                                      </div>
                                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ color: "#94a3b8" }}>Threshold</span>
                                        <span style={{ fontWeight: 600, color: "#fbbf24" }}>
                                          {target.target_type === "percentage"
                                            ? `${target.target_value}%`
                                            : `$${target.target_value.toLocaleString()}`}
                                        </span>
                                      </div>
                                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ color: "#94a3b8" }}>Trigger</span>
                                        <span style={{
                                          fontWeight: 600,
                                          padding: "1px 6px",
                                          borderRadius: 6,
                                          fontSize: 10,
                                          background: target.trigger_mode === "once" ? "rgba(59, 130, 246, 0.2)" : "rgba(168, 85, 247, 0.2)",
                                          color: target.trigger_mode === "once" ? "#93c5fd" : "#c4b5fd",
                                        }}>
                                          {target.trigger_mode === "once" ? "Once only" : "Every time"}
                                        </span>
                                      </div>
                                    </div>
                                    {/* Message */}
                                    {target.message && (
                                      <div style={{
                                        marginTop: 8,
                                        paddingTop: 7,
                                        borderTop: "1px solid rgba(148, 163, 184, 0.2)",
                                      }}>
                                        <div style={{ color: "#94a3b8", fontSize: 10, marginBottom: 3 }}>Message</div>
                                        <div style={{
                                          fontSize: 11,
                                          color: "#e2e8f0",
                                          fontStyle: "italic",
                                          lineHeight: 1.4,
                                          wordBreak: "break-word",
                                        }}>
                                          "{target.message}"
                                        </div>
                                      </div>
                                    )}
                                  </div>,
                                  document.body
                                )}
                              </span>
                            );
                          })()}
                        </span>
                      </td>
                      <td data-seq-private=""
                        style={{
                          position: "sticky",
                          left: COL_NAME,
                          zIndex: 10,
                          background: group.color.bg,
                          padding: "8px",
                          textAlign: "right",
                          fontSize: 11,
                          color: "#64748b",
                          width: COL_BUDGET,
                          minWidth: COL_BUDGET,
                        }}
                      >
                        {formatCurrency(row.budget)}
                      </td>
                      <td
                        style={{
                          position: "sticky",
                          left: COL_NAME + COL_BUDGET,
                          zIndex: 10,
                          background: group.color.bg,
                          padding: "8px",
                          textAlign: "right",
                          fontWeight: 600,
                          fontSize: 11,
                          color: isOverBudget
                            ? "#dc2626"
                            : isNearLimit
                              ? "#d97706"
                              : "#16a34a",
                          width: COL_TOTAL,
                          minWidth: COL_TOTAL,
                        }}
                      >
                        {totalPct % 1 === 0 ? totalPct : totalPct.toFixed(1)}%
                      </td>
                      <td data-seq-private=""
                        style={{
                          position: "sticky",
                          left: COL_NAME + COL_BUDGET + COL_TOTAL,
                          zIndex: 10,
                          background: group.color.bg,
                          padding: "8px",
                          textAlign: "right",
                          fontWeight: 500,
                          fontSize: 11,
                          color: totalAmt > 0 ? "#16a34a" : "#94a3b8",
                          borderRight: "2px solid #cbd5e1",
                          width: COL_CLAIMED,
                          minWidth: COL_CLAIMED,
                        }}
                      >
                        {totalAmt > 0 ? formatCurrency(totalAmt) : "—"}
                      </td>

                      {monthColumns.map(({ month, year }) => {
                        const progressValue = getProgressValue(
                          row.subcategoryId,
                          month,
                          year,
                        );
                        const pct = progressValue?.percentage || 0;

                        // Check for payment discrepancy (split-cell)
                        const cellKey = `${row.subcategoryId}|${month}|${year}`;
                        const paymentInfo = paymentCellMap.get(cellKey);
                        // Show split cell when: action is non-paid (voided, agreement, push)
                        // OR action is "paid" but paid_percentage differs from claimed
                        // Skip "pending" items (no action taken yet)
                        const hasSplitCell = paymentInfo && pct !== 0 && paymentInfo.action !== "pending" && (
                          paymentInfo.action !== "paid" ||
                          Math.abs(paymentInfo.paid_percentage - paymentInfo.claimed_percentage) > 0.01
                        );

                        return (
                          <td
                            key={`${month}-${year}`}
                            {...(group === groupedData[0] && idx === 0 && month === monthColumns[0]?.month && year === monthColumns[0]?.year ? { 'data-tour-id': 'pc-first-cell' } : {})}
                            style={{
                              padding: 4,
                              textAlign: "center",
                              background: group.color.bg,
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setEditModal({
                                subcategoryId: row.subcategoryId,
                                categoryName: group.categoryName,
                                subcategoryName: row.subcategoryName,
                                month,
                                year,
                                budget: row.budget,
                                paymentInfo: paymentInfo || null,
                                totalClaimedSoFar: progress
                                  .filter(
                                    (p) =>
                                      p.subcategory_id === row.subcategoryId &&
                                      !(
                                        p.claim_month === month &&
                                        p.claim_year === year
                                      ),
                                  )
                                  .reduce(
                                    (sum, p) => sum + (Number(p.amount) || 0),
                                    0,
                                  ),
                              })
                          }
                        >
                          {hasSplitCell ? (
                            /* ── Split Cell: Claimed vs Paid ── */
                            <div data-seq-private=""
                              style={{
                                borderRadius: 5,
                                overflow: "hidden",
                                position: "relative",
                                transition: "all 0.15s ease",
                                border: `1px solid ${getActionColor(paymentInfo.action).border}`,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.08)";
                                e.currentTarget.style.boxShadow =
                                  "0 2px 8px rgba(0,0,0,0.15)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                              title={[
                                `Claimed: ${pct}% ($${paymentInfo.claimed_amount.toLocaleString()})`,
                                `${getActionLabel(paymentInfo.action)}: ${paymentInfo.paid_percentage}% ($${paymentInfo.paid_amount.toLocaleString()})`,
                                paymentInfo.action !== "voided" && paymentInfo.claimed_amount - paymentInfo.paid_amount > 0
                                  ? `Unpaid: $${(paymentInfo.claimed_amount - paymentInfo.paid_amount).toLocaleString()}`
                                  : null,
                                paymentInfo.action === "works_not_completed" ? "⏭ Pushed to next month" : null,
                                paymentInfo.notes ? `Note: ${paymentInfo.notes}` : null,
                              ].filter(Boolean).join("\n")}
                            >
                              {/* Top: Claimed */}
                              <div
                                style={{
                                  padding: "2px 4px",
                                  background: "rgba(22, 163, 74, 0.12)",
                                  color: "#16a34a",
                                  fontWeight: 600,
                                  fontSize: 9.5,
                                  lineHeight: 1.3,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 2,
                                  borderBottom: `1.5px solid ${getActionColor(paymentInfo.action).border}`,
                                }}
                              >
                                <span style={{ opacity: 0.7, fontSize: 8, fontWeight: 500 }}>C</span>
                                <span>{pct % 1 === 0 ? pct : pct.toFixed(1)}%</span>
                              </div>
                              {/* Bottom: Paid/Action */}
                              <div
                                style={{
                                  padding: "2px 4px",
                                  background: getActionColor(paymentInfo.action).bg,
                                  color: getActionColor(paymentInfo.action).text,
                                  fontWeight: 700,
                                  fontSize: paymentInfo.action === "voided" || paymentInfo.action === "works_not_completed" ? 8.5 : 9.5,
                                  lineHeight: 1.3,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 2,
                                  textDecoration:
                                    paymentInfo.action === "voided"
                                      ? "line-through"
                                      : "none",
                                }}
                              >
                                <span style={{ opacity: 0.7, fontSize: 8, fontWeight: 500 }}>
                                  {paymentInfo.action === "voided" ? "✕"
                                    : paymentInfo.action === "works_not_completed" ? "⏭"
                                    : paymentInfo.action === "agreement_made" ? "✎"
                                    : "P"}
                                </span>
                                <span>
                                  {paymentInfo.action === "voided"
                                    ? "VOID"
                                    : paymentInfo.action === "works_not_completed"
                                      ? "PUSH"
                                      : paymentInfo.action === "agreement_made"
                                        ? `${paymentInfo.paid_percentage % 1 === 0 ? paymentInfo.paid_percentage : paymentInfo.paid_percentage.toFixed(1)}%`
                                        : `${paymentInfo.paid_percentage % 1 === 0 ? paymentInfo.paid_percentage : paymentInfo.paid_percentage.toFixed(1)}%`}
                                </span>
                              </div>
                            </div>
                          ) : (
                            /* ── Normal Cell ── */
                            <div
                              style={{
                                padding: "6px 4px",
                                borderRadius: 4,
                                background:
                                  pct !== 0
                                    ? pct > 0
                                      ? "rgba(22, 163, 74, 0.15)"
                                      : "rgba(220, 38, 38, 0.15)"
                                    : "rgba(255,255,255,0.5)",
                                color:
                                  pct !== 0
                                    ? pct > 0
                                      ? "#16a34a"
                                      : "#dc2626"
                                    : "#94a3b8",
                                fontWeight: pct !== 0 ? 600 : 400,
                                fontSize: 11,
                                transition: "all 0.15s ease",
                                position: "relative",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.08)";
                                e.currentTarget.style.boxShadow =
                                  "0 2px 8px rgba(0,0,0,0.15)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                              title={
                                paymentInfo && paymentInfo.action === "paid" && pct > 0
                                  ? `✅ Fully Paid: ${paymentInfo.paid_percentage}% ($${paymentInfo.paid_amount.toLocaleString()})`
                                  : undefined
                              }
                            >
                              {pct !== 0
                                ? `${pct % 1 === 0 ? pct : pct.toFixed(1)}%`
                                : "—"}
                              {/* Subtle paid checkmark for fully paid cells */}
                              {paymentInfo && paymentInfo.action === "paid" && pct > 0 && (
                                <span
                                  style={{
                                    position: "absolute",
                                    top: 1,
                                    right: 2,
                                    fontSize: 7,
                                    opacity: 0.5,
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                            </div>
                          )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* SEQ-247: below the table on purpose. It appears after the fetch
          fails, and above the table it pushed the whole grid down. */}
      {paymentLoadError && (
        <div
          role="alert"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            marginTop: 12,
            padding: "8px 12px",
            border: "1px solid var(--color-error)",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            color: "var(--color-error-text)",
            background: "var(--bg-secondary)",
          }}
        >
          <span>
            {retryPhase === "failedAgain"
              ? "Still could not load payment details. Check your connection, then try again."
              : "Could not load payment details, so paid amounts may be missing from this view."}
          </span>
          <button
            ref={paymentRetryBtnRef}
            type="button"
            className="btn btn-secondary btn-sm range-retry-btn"
            aria-disabled={retryPhase === "retrying"}
            aria-busy={retryPhase === "retrying"}
            onClick={() => {
              // aria-disabled, not disabled, so keyboard focus stays on the button.
              if (retryPhase === "retrying") return;
              setRetryPhase("retrying");
              setPaymentRetry((n) => n + 1);
            }}
          >
            {retryPhase === "retrying" ? "Retrying" : "Try again"}
          </button>
        </div>
      )}
      {retryPhase === "recovered" && !paymentLoadError && (
        <p
          ref={paymentRecoveredRef}
          role="status"
          tabIndex={-1}
          style={{ margin: "12px 0 0", fontSize: 13, color: "var(--text-secondary)" }}
        >
          Payment details loaded.
        </p>
      )}

      {/* Legend - Pill style, clickable to scroll */}
      <div
        data-tour-id="pc-category-legend"
        style={{
          display: "flex",
          gap: 8,
          fontSize: 11,
          color: "#64748b",
          marginTop: 10,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <span style={{ color: "#94a3b8" }}>
          💡 Click cell to edit • Click category to jump
        </span>
        <div style={{ flex: 1 }} />
        {groupedData.map((g) => {
          const isActive = activeCategory === g.categoryId;
          return (
            <button
              key={g.categoryId}
              type="button"
              onClick={() => {
                const container = scrollContainerRef.current;
                // Target the first subcategory row (NOT sticky, gives accurate position)
                // This allows correct scroll calculation for both up and down
                const element = container?.querySelector(
                  `[data-first-sub-row="${g.categoryId}"]`,
                ) as HTMLElement | null;

                if (container && element) {
                  isScrollingProgrammatically.current = true;
                  setActiveCategory(g.categoryId);

                  const containerRect = container.getBoundingClientRect();
                  const elementRect = element.getBoundingClientRect();
                  // Offset = Main Header (50px) + Category Header row height (~55px)
                  // This ensures the full category header is visible at the top
                  const totalHeaderOffset = 113;

                  const scrollTarget =
                    container.scrollTop +
                    (elementRect.top - containerRect.top) -
                    totalHeaderOffset;

                  container.scrollTo({
                    top: Math.max(0, scrollTarget),
                    behavior: "smooth",
                  });

                  setTimeout(() => {
                    isScrollingProgrammatically.current = false;
                  }, 600);
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 8px",
                borderRadius: 20,
                background: isActive ? g.color.headerBg : "#f1f5f9",
                border: `1.5px solid ${isActive ? g.color.border : "#e2e8f0"}`,
                transform: isActive ? "scale(1.02)" : "scale(1)",
                transition: "all 0.2s ease",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: g.color.border,
                  transition: "all 0.2s ease",
                }}
              />
              <span
                style={{
                  color: isActive ? g.color.text : "#64748b",
                  fontWeight: isActive ? 600 : 500,
                  fontSize: 10,
                  transition: "all 0.2s ease",
                }}
              >
                {g.categoryName}
              </span>
            </button>
          );
        })}
      </div>

      {/* Edit Modal */}
      {editModal && (
        <EditCellModal
          isOpen={true}
          onClose={() => setEditModal(null)}
          onSave={(value) =>
            handleSaveProgress(
              editModal.subcategoryId,
              editModal.month,
              editModal.year,
              value,
              editModal.budget,
            )
          }
          categoryName={editModal.categoryName}
          subcategoryName={editModal.subcategoryName}
          month={editModal.month}
          year={editModal.year}
          currentValue={
            getProgressValue(
              editModal.subcategoryId,
              editModal.month,
              editModal.year,
            )?.percentage || 0
          }
          budget={editModal.budget}
          saving={saving}
          totalClaimedSoFar={editModal.totalClaimedSoFar}
          paymentInfo={editModal.paymentInfo}
        />
      )}
      {/* Target notification popup */}
      {targetNotification && (
        <TargetNotificationPopup
          notification={targetNotification}
          onClose={() => setTargetNotification(null)}
        />
      )}

      {/* ── Onboarding Tour ── */}
      {tour.activeTour && (
        <TourEngine
          tour={tour.activeTour}
          onComplete={tour.complete}
          onSkip={tour.skip}
        />
      )}
    </>
  );
}
